import { ChevronDown, FileText } from "lucide-react";
import React, { useEffect, useState } from "react";
import { EDSButton } from "../../../eds/EDSButton";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "../../../ui/dialog";

interface DiaryNoteViewerProps {
  noteContent: string;
  onSave: (content: string) => Promise<void>;
  className?: string;
  date?: Date;
}

export const DiaryNoteViewer: React.FC<DiaryNoteViewerProps> = ({
  noteContent,
  onSave,
  className = "",
  date = new Date()
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editableContent, setEditableContent] = useState(noteContent);
  const [isSaving, setIsSaving] = useState(false);

  // Sync editableContent when noteContent changes (after save or date change)
  useEffect(() => {
    setEditableContent(noteContent);
  }, [noteContent]);

  const hasContent = noteContent && noteContent.trim().length > 0;
  const previewText = hasContent ? noteContent.substring(0, 300) : "";
  const needsTruncation = hasContent && noteContent.length > 300;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(editableContent);
    setIsSaving(false);
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setEditableContent(noteContent);
    setIsModalOpen(false);
  };

  return (
    <div
      className={[
        "diary-note-viewer",
        "bg-transparent border-none rounded-lg p-0 m-0 mr-2 touch-manipulation overflow-hidden",
        className
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogTrigger asChild>
          {hasContent ? (
            <div className="pt-0 w-full">
              <div
                className={[
                  "flex items-center gap-2 cursor-pointer",
                  "min-h-[40px] max-md:min-h-[44px]",
                  "px-3 py-2 max-md:px-4 max-md:py-3",
                  "rounded-md transition-colors duration-200 w-full box-border",
                  "hover:bg-[hsl(var(--primary)/0.1)] hover:text-[hsl(var(--accent-foreground))]"
                ].join(" ")}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0 max-w-full">
                  <FileText className="w-4 h-4 max-md:w-[18px] max-md:h-[18px] shrink-0 text-muted-foreground" />
                  <span className="text-[color:var(--foreground,#ffffff)] leading-tight whitespace-nowrap overflow-hidden text-ellipsis flex-1 min-w-0 max-w-full max-md:text-base">
                    {previewText}
                    {needsTruncation && "..."}
                  </span>
                </div>
                <div className="flex items-center shrink-0">
                  {needsTruncation && (
                    <ChevronDown className="w-4 h-4 max-md:w-[18px] max-md:h-[18px] text-muted-foreground transition-transform duration-200" />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <button
              className={[
                "add-note-placeholder",
                "flex items-center gap-2 cursor-pointer",
                "text-muted-foreground",
                "min-h-[36px] max-md:min-h-[44px]",
                "px-3 py-2 max-md:px-4 max-md:py-3",
                "rounded-md transition-colors duration-200 w-full",
                "hover:bg-[hsl(var(--primary)/0.1)] hover:text-[var(--accent-color,#1fd4d4)] hover:cursor-pointer",
                "active:bg-[var(--muted,rgba(32,35,48,0.7))]",
                "focus:outline-2 focus:outline-[var(--accent-color,#1fd4d4)] focus:outline-offset-2"
              ].join(" ")}
              onClick={() => {
                setIsModalOpen(true);
              }}
              type="button"
              aria-label="Add note"
            >
              <span className="text-base font-bold shrink-0 max-md:text-lg">
                +
              </span>
              <span className="text-sm whitespace-nowrap overflow-visible max-md:text-base">
                Add Note
              </span>
            </button>
          )}
        </DialogTrigger>
        <DialogContent
          className="diary-note-modal max-h-[85vh] overflow-hidden flex flex-col"
          showCloseButton={true}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Diary Note - {formatDate(date)}
            </DialogTitle>
          </DialogHeader>

          <div className="my-4 mx-0 flex-1 min-h-0 w-full">
            <textarea
              id="diary-note-textarea"
              value={editableContent}
              onChange={(e) => setEditableContent(e.target.value)}
              className="diary-note-textarea h-full min-h-[200px] max-h-[60vh] w-full rounded-[calc(var(--radius)-2px)] border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm leading-5 text-[hsl(var(--foreground))] shadow-sm transition-colors duration-200 resize-y font-[inherit] box-border block m-0 placeholder:text-[hsl(var(--muted-foreground))] placeholder:text-sm focus:outline-none focus:border-[hsl(var(--ring))] focus:shadow-[0_0_0_2px_hsl(var(--ring)/0.2)] disabled:cursor-not-allowed disabled:opacity-50 max-md:text-base max-md:p-4 max-md:min-h-[200px]"
              placeholder="Enter your note here..."
              autoFocus
            />
          </div>

          <DialogFooter>
            <EDSButton
              variant="secondary"
              onClick={handleCancel}
              className="action-button"
              disabled={isSaving}
            >
              Cancel
            </EDSButton>
            <EDSButton
              variant="default"
              onClick={handleSave}
              className="action-button primary-button"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </EDSButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
